import React, { useEffect, useRef } from 'react';
import { ChatMessage, LoadingStatus } from '../../types';
import './MessageList.css';

interface MessageListProps {
  messages: ChatMessage[];
  isLoading: boolean;
  loadingStatus: LoadingStatus;
  activeFunctionLabel: string | null;
  showFunctionDetails?: boolean;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading,
  loadingStatus,
  activeFunctionLabel,
  showFunctionDetails = false,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const renderFormattedMessage = (content: string) => {
    const lines = content.split('\n');

    return lines.map((line, lineIndex) => {
      const boldParts = line.split(/(\*\*.*?\*\*)/g);

      return (
        <React.Fragment key={`line-${lineIndex}`}>
          {boldParts.map((part, partIndex) => {
            const boldMatch = part.match(/^\*\*(.*?)\*\*$/);
            if (boldMatch) {
              return <strong key={`part-${lineIndex}-${partIndex}`}>{boldMatch[1]}</strong>;
            }
            return <React.Fragment key={`part-${lineIndex}-${partIndex}`}>{part}</React.Fragment>;
          })}
          {lineIndex < lines.length - 1 ? <br /> : null}
        </React.Fragment>
      );
    });
  };

  const getMessageFunctionLabel = (message: ChatMessage): string | null => {
    const metadata = message.metadata || {};

    const directLabel = metadata.executed_function || metadata.function_name;
    if (typeof directLabel === 'string' && directLabel.trim()) return directLabel.trim();

    const execution = metadata.execution;
    if (execution && typeof execution === 'object' && typeof execution.function_name === 'string') {
      return execution.function_name;
    }

    return null;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getLoadingMessage = () => {
    switch (loadingStatus) {
      case 'thinking':
        return 'Analizando tu consulta...';
      case 'executing':
        return 'Ejecutando funciones...';
      case 'processing':
        return 'Procesando resultados...';
      default:
        return 'Escribiendo...';
    }
  };

  const getLoadingIcon = () => {
    switch (loadingStatus) {
      case 'thinking':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3"></circle>
            <circle cx="12" cy="1" r="1"></circle>
            <circle cx="12" cy="23" r="1"></circle>
            <circle cx="4.22" cy="4.22" r="1"></circle>
            <circle cx="19.78" cy="19.78" r="1"></circle>
            <circle cx="1" cy="12" r="1"></circle>
            <circle cx="23" cy="12" r="1"></circle>
            <circle cx="4.22" cy="19.78" r="1"></circle>
            <circle cx="19.78" cy="4.22" r="1"></circle>
          </svg>
        );
      case 'executing':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
          </svg>
        );
      case 'processing':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
          </svg>
        );
      default:
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        );
    }
  };

  return (
    <div className="message-list">
      {messages.length === 0 && !isLoading && (
        <div className="empty-state">
          <div className="empty-logo">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </div>
          <h3>Asistente de Suministros Industriales</h3>
          <p>Escribe un mensaje para comenzar</p>
        </div>
      )}

      {messages.map((message) => (
        <div
          key={message.id}
          className={`message-row ${message.sender === 'user' ? 'message-row-user' : 'message-row-bot'}`}
        >
          <div className="message-row-inner">
            <div className="message-avatar">
              {message.sender === 'user' ? (
                <div className="avatar avatar-user">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
              ) : (
                <div className="avatar avatar-bot">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
              )}
            </div>
            <div className="message-body">
              <span className="message-sender">
                {message.sender === 'user' ? 'Tú' : 'Asistente'}
              </span>
              <div className="message-text">{renderFormattedMessage(message.content)}</div>
              {showFunctionDetails && message.sender === 'bot' && getMessageFunctionLabel(message) && (
                <div className="message-function-chip">
                  Función ejecutada: {getMessageFunctionLabel(message)}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {isLoading && (
        <div className="message-row message-row-bot">
          <div className="message-row-inner">
            <div className="message-avatar">
              <div className={`avatar avatar-bot ${loadingStatus}`}>
                {getLoadingIcon()}
              </div>
            </div>
            <div className="message-body">
              <span className="message-sender">Asistente</span>
              <div className="function-indicator">
                <div className="function-indicator-text">
                  {getLoadingMessage()}
                </div>
                {showFunctionDetails && activeFunctionLabel && (
                  <div className="function-indicator-name">
                    {activeFunctionLabel}
                  </div>
                )}
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
